import { Badge, Box, Flex, Table as MantineTable, Text } from "@mantine/core"
import { EyeIcon, FilePlusIcon, LockIcon } from "@phosphor-icons/react"
import { forwardRef, ForwardRefRenderFunction, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { DeliveryApi, DropShippingApi } from "../../apis"
import { IOrderDropShippingPlus } from "../../models/Order"
import { IGeneral } from "../../store/General"
import { dateTimeSecFormat } from "../../utils/date"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { CustomsTable, ITableRef } from "../custom-table"
import { ControlPanel } from "../odoo/control-panel"
import { GroupByOption } from "../odoo/group-by-menu"
import { usePermission } from "../permission"
import { ListColumn, ListHead, nextSort, RowActionIcons } from "../purchase-sales/list-table"
import { OrderStatusTag } from "../purchase-sales/status-tag"
import { Sort } from "../table"
import RenderText from "../ui/render-text"
import { DropShippingFilterPanel } from "./dropshipping-filter-panel"
import { DropShippingFilters, useDropShippingFilters } from "./dropshipping-filters"
import classes from "./list.module.css"
import { EmptyStateGuide, nextStepToast, useHelpContext } from "@dehub/theme"

const HELP = "order.supplier-drop-shipping-list"

/** `partner`: «Миний барааг түнш зарсан» (isDropshipping=true). `mine`: «Түншийн барааг би зарсан» (isDropshipping=false). */
export type DropShippingMode = "partner" | "mine"

export type SupplierDropShippingRef = {
  getFilter: () => DropShippingFilters & { isDropshipping: boolean }
  getOrder: () => [string, "ASC" | "DESC"]
}

/** The one search box: SO / PO numbers by prefix, plain digits are a SO number, anything else a partner name. */
function searchToFilter(text: string): Partial<DropShippingFilters> {
  const q = text.trim()
  if (!q) return {}
  if (/^po/i.test(q)) return { purchaseCode: q }
  if (/^so/i.test(q) || /^\d+$/.test(q)) return { salesCode: q }
  return { receiverBusinessPartnerRefCodeBusinessName: q }
}

const Component: ForwardRefRenderFunction<SupplierDropShippingRef, { mode: DropShippingMode }> = ({ mode }, ref) => {
  const { t } = useTranslation(["supplierSales", "order", "common"])
  const navigate = useNavigate()
  const tableRef = useRef<ITableRef>(null)
  const { orderStatus, paymentStatus, deliveryNoteStatus, orderTypes, orderPurchaseTypes } = useSelector((s: { general: IGeneral }) => s.general)

  const ORD_DRSHPING_DETAIL = usePermission("ORD_DRSHPING_DETAIL") // BE order/dropshipping/get.ts
  const ORD_DN_CREATE = usePermission("ORD_DN_CREATE") // BE delivery-note/create.ts
  const ERP_PGH_MARGIN_VIEW = usePermission("ERP_PGH_MARGIN_VIEW")
  const { filters, setFilter, clearFilters, activeFilterChips, searchQuery } = useDropShippingFilters()
  const [sorted, setSorted] = useState<[string, Sort]>(["createdAt", Sort.DESC])
  const [filterOpen, setFilterOpen] = useState(false)
  const [groupBy, setGroupBy] = useState<string | null>(null)
  const [creatingId, setCreatingId] = useState<string | null>(null)
  const [applied, setApplied] = useState<Partial<DropShippingFilters>>({})
  const [rows, setRows] = useState<IOrderDropShippingPlus[]>([])
  // @dehub/help: side for the status content; counts for the «Одоо» cards.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    const deliverMineCount = rows.filter((r) => r.deliveryBy === "DROPSHIPPER" && !r.deliveryNote).length
    const dnNeededCount = rows.filter((r) => r.deliveryBy === "DROPPER" && !r.deliveryNote).length
    setHelpContext({ side: mode === "partner" ? "RECEIVER" : "SENDER", vars: { count: rows.length, deliverMineCount, dnNeededCount } })
  }, [rows, mode, setHelpContext])

  const isDropshipping = mode === "partner"
  const requestFilters = useMemo(() => ({ ...filters, ...applied, isDropshipping }), [filters, applied, isDropshipping])

  const GROUP_BY_OPTIONS: GroupByOption[] = [
    { key: "orderStatus", label: t("supplierSales:dropshipping.columns.status") },
    { key: "type", label: t("supplierSales:dropshipping.columns.type") },
    { key: "paymentStatus", label: t("supplierSales:dropshipping.columns.paymentStatus") },
  ]

  const reload = () => tableRef.current?.reload()
  const clearAll = () => {
    clearFilters()
    setFilter("searchQuery", "")
    setApplied({})
  }

  const onCreateDelivery = async (record: IOrderDropShippingPlus) => {
    if (creatingId) return
    setCreatingId(record.id!)
    try {
      await DeliveryApi.create([record.id!])
      nextStepToast(`${HELP}.toast.create-dn`, { fallback: { title: t("supplierSales:dropshipping.deliveryNoteCreated") } })
      reload()
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    }
    setCreatingId(null)
  }

  useImperativeHandle(ref, () => ({ getFilter: () => requestFilters as never, getOrder: () => sorted }), [requestFilters, sorted])

  const c = (key: string) => t(`supplierSales:dropshipping.columns.${key}`)
  const columns: ListColumn<IOrderDropShippingPlus>[] = [
    { id: "so", label: c("soNumber"), sort: "salesCode", render: (r) => <Text ff="monospace" fw={700} fz={14}>{r.salesCode || "-"}</Text> },
    { id: "po", label: c("poNumber"), sort: "purchaseCode", render: (r) => <Text ff="monospace" fz={14}>{r.purchaseCode || "-"}</Text> },
    { id: "date", label: c("date"), sort: "createdAt", render: (r) => <RenderText text={r.createdAt ? dateTimeSecFormat(r.createdAt) : null} /> },
    { id: "status", label: c("status"), sort: "orderStatus", render: (r) => <OrderStatusTag code={r.orderStatus} /> },
    {
      id: "customer", label: t("supplierSales:dropshipping.customer"), sort: "receiverBusinessPartnerRefCodeBusinessName",
      render: (r) => (
        <Box>
          <Text fz={14} fw={600}>{renderBusinessName(r.receiverBusiness?.partner, "") || renderBusinessName(r.receiverBusiness, "") || "-"}</Text>
          <Text fz={14} c="gray.8" ff="monospace">{r.receiverBusiness?.partner?.refCode || r.receiverBusiness?.refCode || "-"}</Text>
        </Box>
      ),
    },
    { id: "total", label: c("totalAmount"), sort: "totalAmount", align: "right", render: (r) => <RenderText text={r.totalAmount || 0} isAmount fontWeight={700} /> },
    { id: "sales", label: c("dropperPriceTotal"), align: "right", render: (r) => <RenderText text={r.dropperPriceTotal ?? null} isAmount /> },
    // The backend only sends the margin to holders of ERP_PGH_MARGIN_VIEW (never a 403), so a missing value is «-».
    { id: "margin", label: c("margin"), align: "right", render: (r) => <RenderText text={ERP_PGH_MARGIN_VIEW.hasAccess && r.margin != null ? r.margin : null} isAmount /> },
    {
      id: "deliveryBy", label: c("deliveryBy"),
      render: (r) => r.deliveryBy
        ? <Badge variant="light" color={r.deliveryBy === "DROPPER" ? "blue" : "grape"} size="lg" tt="none" fw={500}>{t(`supplierSales:dropshipping.deliveryBy.${r.deliveryBy}`, r.deliveryBy) as string}</Badge>
        : <RenderText text={null} />,
    },
  ]

  const rowActions = (record: IOrderDropShippingPlus) => {
    const path = mode === "partner" ? `/supplier-drop-shipping/delivery/detail/${record.id}` : `/supplier-drop-shipping/detail/${record.id}`
    return [
      { key: "detail", label: t("supplierSales:dropshipping.detail"), icon: <EyeIcon size={18} />, reason: ORD_DRSHPING_DETAIL.reason, onClick: () => navigate(path) },
      ...(mode === "mine" && record.deliveryBy === "DROPPER" && !record.deliveryNote
        ? [{ key: "dn", label: t("supplierSales:dropshipping.createDeliveryNote"), icon: <FilePlusIcon size={18} />, reason: ORD_DN_CREATE.reason, onClick: () => onCreateDelivery(record) }]
        : []),
    ]
  }

  const keyOf = (r: IOrderDropShippingPlus): string => {
    if (groupBy === "orderStatus") return r.orderStatus ?? ""
    if (groupBy === "type") return r.type ?? ""
    if (groupBy === "paymentStatus") return r.invoice?.paymentStatus ?? ""
    return ""
  }
  const groupLabel = (key: string): string => {
    if (!key) return "-"
    if (groupBy === "orderStatus") return t(`order:orderStatus.${key}`, { defaultValue: key })
    if (groupBy === "type") return orderTypes?.find((o: { code: string; name?: string }) => o.code === key)?.name ?? key
    return paymentStatus?.find((o: { code: string; name?: string }) => o.code === key)?.name ?? key
  }

  return (
    <Flex direction="column" gap="md">
      <ControlPanel
        searchValue={searchQuery}
        onSearchChange={(v) => setFilter("searchQuery", v)}
        onSearch={(v) => setApplied(searchToFilter(v))}
        filterChips={activeFilterChips}
        onFilterClick={() => setFilterOpen(!filterOpen)}
        filterOpen={filterOpen}
        groupByOptions={GROUP_BY_OPTIONS}
        activeGroupBy={groupBy}
        onGroupByChange={setGroupBy}
      />

      <DropShippingFilterPanel
        opened={filterOpen}
        filters={filters}
        setFilter={setFilter}
        clearFilters={clearAll}
        orderStatus={orderStatus}
        paymentStatus={paymentStatus}
        deliveryNoteStatus={deliveryNoteStatus}
        orderTypes={orderTypes}
        orderPurchaseTypes={orderPurchaseTypes}
        onSearch={reload}
      />

      <CustomsTable
        ref={tableRef}
        name={mode === "partner" ? "supplier-drop-shipping.list" : "supplier-drop-shipping-delivery.list"}
        sorted={sorted}
        filters={requestFilters as never}
        emptyTitle={t("supplierSales:dropshipping.emptyTitle")}
        emptyDescription={mode === "partner" ? t("supplierSales:dropshipping.emptyPartner") : t("supplierSales:dropshipping.emptyMine")}
        emptyAction={undefined}
        // Content-driven empty state per tab (spec 10 §2.10); filtered «no results» keeps the default.
        renderEmpty={(state) => (state === "empty" ? <EmptyStateGuide helpKey={`${HELP}.empty.main`} when={mode === "partner" ? "SOLD_BY_PARTNER" : "SOLD_BY_ME"} /> : null)}
        onResult={(data: { rows: IOrderDropShippingPlus[] }) => setRows(data?.rows ?? [])}
        headWhenEmpty
        onClearFilters={clearAll}
        loadData={(data) => DropShippingApi.list(data!)}
        thead={<ListHead columns={columns.map((col) => ({ ...col, helpKey: `${HELP}.field.col-${col.id}` }))} actionLabel={t("common:table.action")} sorted={sorted} onSort={(k) => setSorted((s) => nextSort(s, k))} />}
        tbody={(rows: IOrderDropShippingPlus[]) => {
          const ordered = groupBy ? [...rows].sort((a, b) => keyOf(a).localeCompare(keyOf(b))) : rows
          let lastKey: string | null = null
          return (
            <MantineTable.Tbody>
              {ordered.map((record, index) => {
                const k = groupBy ? keyOf(record) : null
                const header = groupBy && k !== lastKey
                lastKey = k
                return (
                  <Fragment key={record.id ?? index}>
                    {header && (
                      <MantineTable.Tr className={classes.groupRow}>
                        <MantineTable.Td colSpan={columns.length + 1}>{groupLabel(k ?? "")}</MantineTable.Td>
                      </MantineTable.Tr>
                    )}
                    <MantineTable.Tr>
                      <MantineTable.Td><RowActionIcons items={rowActions(record)} /></MantineTable.Td>
                      {columns.map((col) => (
                        <MantineTable.Td key={col.id} ta={col.align === "right" ? "right" : undefined}>{col.render(record, index)}</MantineTable.Td>
                      ))}
                    </MantineTable.Tr>
                  </Fragment>
                )
              })}
            </MantineTable.Tbody>
          )
        }}
      />

      {!ERP_PGH_MARGIN_VIEW.hasAccess && (
        <Box className={classes.note}>
          <LockIcon size={16} />
          <Text fz={14} c="gray.8">{t("supplierSales:dropshipping.marginNote")}</Text>
        </Box>
      )}
    </Flex>
  )
}

export const SupplierDropShipping = forwardRef(Component)
